import React, { type ComponentType, useCallback } from "react";
import { Grid as GridBase, Icon, IconButton, Paper } from "@mui/material";

import ConfirmLocationItem from "@/features/campaigns/components/ConfirmLocationItem";
import { getFormatDate } from "@/helpers/utils";
import CustomerList from "../join-campaign/CustomerList";
import { EMAIL_CAMPAIGN_CONST } from "@/features/campaigns/email/utils/emailCampaignConstants";
import RpSvgIcon from "@/components/icons/RpSvgIcon";
import { RpSweetAlert } from "@/components/alerts/RpSweetAlert";

type EmailTemplate = {
  id: string | number;
  title: string;
  customer_list: string;
  contact_count: string | number;
};

type EmailCampaignSelection = {
  locationInfo?: {
    id?: string | number;
    name?: string;
  };
  template?: string | number;
  schedule_type?: string;
  schedule_date?: string;
  schedule_time?: string;
};

type EmailCampaignConfirmLocProps = {
  data: EmailCampaignSelection;
  onDeleteLoc?: (_locationId?: string | number) => void | Promise<void>;
};

const Grid = GridBase as ComponentType<any>;

function EmailCampaignConfirmLoc({
  data,
  onDeleteLoc,
}: EmailCampaignConfirmLocProps) {
  const templateData = EMAIL_CAMPAIGN_CONST.TEMPLATES.find(
    (t) => t.id === data.template
  ) as EmailTemplate;
  const isImmediate =
    EMAIL_CAMPAIGN_CONST.SCHEDULED_TYPE.NOW.value === data.schedule_type;

  const handleOnDelete = useCallback(() => {
    RpSweetAlert.fire({
      text: "Are you sure you want to remove the selections for this location?",
      imageUrl: "/img/icons/info.svg",
      imageWidth: 80,
      imageHeight: 80,
      imageAlt: "Alert",
      showCancelButton: true,
      confirmButtonText: "Remove",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await onDeleteLoc?.(data?.locationInfo?.id);
      }
    });
  }, [data?.locationInfo?.id, onDeleteLoc]);

  return (
    <Paper className="my-4 py-3 px-2" elevation={2}>
      <Grid container spacing={2}>
        <Grid
          item
          size={{
            xs: 12,
            lg: 3,
            xl: 3,
          }}
        >
          <ConfirmLocationItem
            svgIcon="location_mark"
            label="Location"
            value={data?.locationInfo?.name}
          />
        </Grid>
        <Grid
          item
          size={{
            xs: 6,
            lg: 3,
            xl: 3,
          }}
        >
          <ConfirmLocationItem
            svgIcon="month_calender"
            label="Scheduled Date & Time"
            value={
              isImmediate
                ? data.schedule_type
                : `${getFormatDate(data.schedule_date, "MMM Do, YYYY")} - ${data.schedule_time}`
            }
          />
        </Grid>
        <Grid
          item
          size={{
            xs: 6,
            lg: 2,
            xl: 2,
          }}
        >
          <ConfirmLocationItem
            svgIcon="month_calender"
            label="Selected Template"
            value={templateData.title}
          />
        </Grid>

        <Grid
          item
          size={{
            xs: 6,
            md: 12,
            lg: 3,
            xl: 3,
          }}
        >
          <ConfirmLocationItem
            svgIcon="wallet"
            label="Customer List"
            value={
              <CustomerList
                label={`${templateData.customer_list}`}
                contactNo={templateData.contact_count}
              />
            }
            valueProps={{
              color: "primary",
            }}
          />
        </Grid>
        <Grid
          item
          size={{
            xs: 12,
            lg: 1,
            xl: 1,
          }}
          className="d-flex align-items-center justify-content-end pr-xl-4"
        >
          <IconButton onClick={handleOnDelete} className="p-0">
            <Icon component={RpSvgIcon} name="trash" color="error" />
          </IconButton>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default EmailCampaignConfirmLoc;
